import { TREND_SIGNAL_KEYS } from '../constants';
import type { SpectralWorkspaceSession, TrendSignalKey } from '../types';
import { filterSessionEventsForSegment } from './resolveSpectraSegment';
import { getTrendSignalValue } from './featureValue';

export type SessionFeatureMean = {
  sessionId: number;
  patientCode: string | null | undefined;
  value: number;
};

export type DescriptiveStatRow = {
  feature: TrendSignalKey;
  sessionCount: number;
  mean: number | null;
  std: number | null;
  min: number | null;
  max: number | null;
  coefficientOfVariation: number | null;
  outlierSessions: Array<{
    sessionId: number;
    patientCode: string | null | undefined;
    value: number;
    distance: number;
  }>;
};

const meanOf = (values: number[]) =>
  values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : null;

const sampleStdOf = (values: number[], mean: number | null) => {
  if (values.length < 2 || mean == null) return null;

  return Math.sqrt(
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
      (values.length - 1)
  );
};

const computeSessionFeatureMean = (
  session: Pick<
    SpectralWorkspaceSession,
    'sessionId' | 'patientCode' | 'events'
  >,
  feature: TrendSignalKey,
  startSecond: number,
  endSecond: number
): SessionFeatureMean | null => {
  const events = filterSessionEventsForSegment(
    session.events ?? [],
    startSecond,
    endSecond
  );

  const values = events
    .map(event => getTrendSignalValue(event, feature))
    .filter(
      (value): value is number =>
        typeof value === 'number' && Number.isFinite(value)
    );

  const mean = meanOf(values);
  if (mean == null) return null;

  return {
    sessionId: session.sessionId,
    patientCode: session.patientCode,
    value: mean,
  };
};

const buildFeatureRow = (
  feature: TrendSignalKey,
  sessionValues: SessionFeatureMean[]
): DescriptiveStatRow => {
  const values = sessionValues.map(item => item.value);
  const mean = meanOf(values);
  const std = sampleStdOf(values, mean);
  const coefficientOfVariation =
    mean != null && std != null && Math.abs(mean) > 0
      ? (std / Math.abs(mean)) * 100
      : null;
  const outlierSessions =
    mean == null
      ? []
      : sessionValues
          .map(item => ({
            ...item,
            distance: Math.abs(item.value - mean),
          }))
          .sort((left, right) => right.distance - left.distance)
          .slice(0, 3);

  return {
    feature,
    sessionCount: sessionValues.length,
    mean,
    std,
    min: values.length > 0 ? Math.min(...values) : null,
    max: values.length > 0 ? Math.max(...values) : null,
    coefficientOfVariation,
    outlierSessions,
  };
};

export const computeDescriptiveStatistics = (
  sessions: Pick<
    SpectralWorkspaceSession,
    'sessionId' | 'patientCode' | 'events'
  >[],
  startSecond: number,
  endSecond: number
): DescriptiveStatRow[] =>
  TREND_SIGNAL_KEYS.map(feature => {
    const sessionValues = sessions
      .map(session =>
        computeSessionFeatureMean(session, feature, startSecond, endSecond)
      )
      .filter((item): item is SessionFeatureMean => item != null);

    return buildFeatureRow(feature, sessionValues);
  });
